import { SIcon, SLoader, SPagination, STag } from "@avalara/skylab-sdk/react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import { loadDataTransfers, selectDataTransfer } from "../../app/dataTransferSlice";
import { selectSession } from "../../app/sessionSlice";
import type { SessionState } from "../../app/sessionSlice/types";
import type { AppDispatch } from "../../app/types";
import toast from "../../hooks/toast";
import { rowsPerPageDefault } from "../../shared/constants";
import DataGrid from "../../shared/dataGrid/DataGrid";
import type { GridHeader } from "../../shared/dataGrid/types";
import { redirectToLoginPage } from "../../shared/sessionUtility";
import { formatDate, getRowsPerPage } from "../../shared/Utils";
import NoData from "../search/NoData";
import type { NoDataProps } from "../search/types";
import { getDataTransferDownloadUrl } from "./dataTransferApi";
import type { DataTransfer, DataTransferFileId } from "./types";

const [showToast] = toast();

const EMPTY_VALUE = "—";
// No column is sortable: the API returns newest first.
const SORT_COLUMN: [string, boolean] = ["", true];
const noopSort = (): void => undefined;

interface PaginationEvent {
    detail: {
        currentPage: number;
        rowsPerPage: number;
        flag: string;
    };
}

const NoDataMessage = NoData as React.ComponentType<NoDataProps>;

const ACTIVITY_DISPLAY: Record<string, { label: string; icon: string }> = {
    E: { label: "Export", icon: "export" },
    I: { label: "Import", icon: "import" },
};

const getStatusTagColor = (status: string): string => {
    switch (status.toUpperCase()) {
        case "COMPLETE":
            return "green-dark";
        case "FAILED":
            return "red-lighter";
        case "RUNNING":
            return "green-light";
        default:
            return "yellow-light";
    }
};

const formatTimestamp = (value: string): string =>
    value && !Number.isNaN(new Date(value).getTime()) ? formatDate({ date: value }) : EMPTY_VALUE;

// The package (.avlr) keeps filling with certificate images during this phase, so it is not ready
// to download yet. The log file is unaffected and stays downloadable. Matches CC's fileFormatter.
const PACKAGE_PENDING_STATUS = "PROCESSING - PENDING SYNC";

const isPackagePending = (status: string | null): boolean =>
    (status ?? "").trim().toUpperCase() === PACKAGE_PENDING_STATUS;

interface ApiError {
    response?: { status?: number; data?: { title?: string } };
}

const TransferHistoryGrid: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const session = useSelector(selectSession) as SessionState;
    const preferredPageSize = session.rowsPerPage ?? rowsPerPageDefault;
    const { items, total, page, pageSize, isLoading } = useSelector(selectDataTransfer);
    const initialLoadStarted = useRef(false);

    useEffect(() => {
        if (initialLoadStarted.current) {
            return;
        }
        initialLoadStarted.current = true;
        dispatch(loadDataTransfers(1, preferredPageSize));
    }, [dispatch, preferredPageSize]);

    const paginate = useCallback(
        (event: PaginationEvent): void => {
            if (event.detail.currentPage !== 0 && event.detail.flag !== "") {
                dispatch(loadDataTransfers(event.detail.currentPage, event.detail.rowsPerPage));
            }
        },
        [dispatch]
    );

    const handleDownload = useCallback(
        async (id: number, fileId: DataTransferFileId): Promise<void> => {
            try {
                const url = await getDataTransferDownloadUrl(id, fileId);
                window.open(url, "_blank", "noopener,noreferrer");
            } catch (error) {
                if ((error as ApiError)?.response?.status === 401) {
                    redirectToLoginPage();
                } else {
                    showToast(
                        "error",
                        (error as ApiError)?.response?.data?.title || "Failed to download."
                    );
                }
            }
        },
        []
    );

    const downloadLink = useCallback(
        (
            id: number,
            filename: string | null,
            fileId: DataTransferFileId,
            testId: string
        ): React.ReactNode =>
            filename ? (
                <button
                    type="button"
                    className="link"
                    data-testid={`${testId}-${id}`}
                    onClick={() => handleDownload(id, fileId)}>
                    {filename}
                </button>
            ) : (
                EMPTY_VALUE
            ),
        [handleDownload]
    );

    const gridHeaders: GridHeader<DataTransfer>[] = useMemo(
        () => [
            {
                id: "activity",
                label: "Activity",
                render: (row: DataTransfer) => {
                    const activity = ACTIVITY_DISPLAY[row.activity];
                    return activity ? (
                        <span className="flex align-items-center">
                            <SIcon
                                name={activity.icon}
                                className="margin-right-xs"
                                aria-hidden="true"
                            />
                            {activity.label}
                        </span>
                    ) : (
                        <>{row.activity ?? EMPTY_VALUE}</>
                    );
                },
            },
            {
                id: "filename",
                label: "File name",
                render: (row: DataTransfer) => {
                    if (!row.filename) {
                        return <>{EMPTY_VALUE}</>;
                    }
                    // The package keeps filling during PENDING SYNC, so show the name but not a
                    // download link yet.
                    return isPackagePending(row.status) ? (
                        <span aria-disabled="true" title="The package is still being prepared.">
                            {row.filename}
                        </span>
                    ) : (
                        <>
                            {downloadLink(
                                row.id,
                                row.filename,
                                "package",
                                testIDs.dataTransfer.packageDownload
                            )}
                        </>
                    );
                },
            },
            {
                id: "companySummary",
                label: "Company Data",
                render: (row: DataTransfer) => <>{row.companySummary || EMPTY_VALUE}</>,
            },
            {
                id: "includes",
                label: "Includes",
                render: (row: DataTransfer) => <>{row.includes?.join(", ") || EMPTY_VALUE}</>,
            },
            {
                id: "status",
                label: "Status",
                render: (row: DataTransfer) =>
                    row.status ? (
                        <STag color={getStatusTagColor(row.status)}>
                            <span slot="content">{row.status}</span>
                        </STag>
                    ) : (
                        <>{EMPTY_VALUE}</>
                    ),
            },
            {
                id: "logFile",
                label: "Log file",
                render: (row: DataTransfer) => (
                    <>
                        {downloadLink(row.id, row.logFile, "log", testIDs.dataTransfer.logDownload)}
                    </>
                ),
            },
            {
                id: "requestingUser",
                label: "User account",
                render: (row: DataTransfer) => <>{row.requestingUser || EMPTY_VALUE}</>,
            },
            {
                id: "created",
                label: "Created",
                render: (row: DataTransfer) => <>{formatTimestamp(row.created)}</>,
            },
            {
                id: "modified",
                label: "Last Modified",
                render: (row: DataTransfer) => <>{formatTimestamp(row.modified)}</>,
            },
        ],
        [downloadLink]
    );

    return (
        <div data-testid={testIDs.dataTransfer.historyGrid}>
            <h2 className="margin-top-none">Transfer history</h2>

            {isLoading ? <SLoader loading aria-live="polite" /> : null}
            {!isLoading && total === 0 ? (
                <div data-testid={testIDs.dataTransfer.historyEmpty}>
                    <NoDataMessage showGenNoDataMsg />
                </div>
            ) : null}
            {!isLoading && total > 0 ? (
                <>
                    <DataGrid
                        gridHeaders={gridHeaders}
                        gridData={items}
                        sortFunction={noopSort}
                        gridName="transfers"
                        totalRecords={total}
                        sortColumn={SORT_COLUMN}
                    />
                    <SPagination
                        className="margin-top-md"
                        rowsPerPage={getRowsPerPage({ totalRecords: total }, pageSize) as number}
                        totalRecords={total}
                        startIndex={(page - 1) * pageSize}
                        onS-paginate={paginate}
                    />
                </>
            ) : null}
        </div>
    );
};

export default TransferHistoryGrid;
